import { RouteRecordRaw } from 'vue-router';
import UploadFoldersPage from '@/VueComponents/PageComponents/UploadFolders/UploadFoldersPage.vue';
import UploadFolderDetailsPageLoader from '@/VueComponents/PageComponents/UploadFolderDetails/UploadFolderDetailsPageLoader.vue';
import RequestsPage from '@/VueComponents/Requests/RequestsPage.vue';
import CamerasPage from '@/VueComponents/PageComponents/Cameras/CamerasPage.vue';
import CameraDetailsPage from '@/VueComponents/PageComponents/CameraDetails/CameraDetailsPage.vue';
import AddCameraPage from '@/VueComponents/PageComponents/AddCamera/AddCameraPage.vue';
import GroupsPage from '@/VueComponents/PageComponents/Groups/GroupsPage.vue';
import GroupDetails from '@/VueComponents/Groups/GroupDetails.vue';
import UsersPage from '@/VueComponents/PageComponents/Users/UsersPage.vue';
import UserDetails from '@/VueComponents/PageComponents/Users/UserDetails.vue';
import MyAccountPage from '@/VueComponents/PageComponents/MyAccount/MyAccountPage.vue';
import RequestDetailsPageLoader from '@/VueComponents/PageComponents/RequestDetails/RequestDetailsPageLoader.vue';
import PortalSettingsProvider from '@/Utils/portalSettingsProvider';
import { AccountType } from '@/Types/Enums/accountType';
import { PreviousRoute } from './previousRoute';
import PortalError from '@/VueComponents/PageComponents/Portal/PortalError.vue';
import PageNotAvailable from '@/VueComponents/PageComponents/Portal/PageNotAvailable.vue';
import { useContextDataStore } from '@/VueCore/stores/contextDataStore';
import { PortalStartupErrorType } from '@/Types/Enums/portalStartupErrorType';
import constants from '@/constants';

const pageMode = constants.pageMode;

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'Home',
    component: RequestsPage,
    meta: { pageTitleKey: 'Requests' },
    props: route => ({
      previousRoute: route.params.previousRoute as PreviousRoute
    })
  },
  {
    path: '/requests',
    name: 'Requests',
    component: RequestsPage,
    meta: { pageTitleKey: 'Requests' },
    props: route => ({
      previousRoute: route.params.previousRoute as PreviousRoute
    })
  },
  {
    path: '/request/:id',
    name: 'RequestDetails',
    component: RequestDetailsPageLoader,
    meta: { pageTitleKey: 'RequestDetails' }
  },
  {
    path: '/uploadfolders',
    name: 'UploadFolders',
    component: UploadFoldersPage,
    meta: { pageTitleKey: 'UploadFolders' },
    beforeEnter: (to, from, next) => {
      if (PortalSettingsProvider.getAccountType() !== AccountType.Partner || !PortalSettingsProvider.getSelfResponseEnabled()) {
        next('/');
        return;
      }

      next();
    },
    props: route => ({
      previousRoute: route.params.previousRoute
    })
  },
  {
    path: '/uploadfolderdetails/:id',
    name: 'UploadFolderDetails',
    component: UploadFolderDetailsPageLoader,
    meta: { pageTitleKey: 'SelfResponseDetails' },
    beforeEnter: (to, from, next) => {
      if (PortalSettingsProvider.getAccountType() !== AccountType.Partner || !PortalSettingsProvider.getSelfResponseEnabled()) {
        next('/');
        return;
      }

      next();
    }
  },
  {
    path: '/cameras',
    name: 'Cameras',
    component: CamerasPage,
    meta: { pageTitleKey: 'CamerasTitle' },
    beforeEnter: async (to, from, next) => {
      if (PortalSettingsProvider.getAccountType() !== AccountType.Business) {
        next('/');
        return;
      }

      next();
    },
    props: route => ({
      previousRoute: route.params.previousRoute as PreviousRoute
    })
  },
  {
    path: '/cameras/new',
    name: 'AddCamera',
    component: AddCameraPage,
    meta: { pageTitleKey: 'AddCameraTitle' },
    beforeEnter: async (to, from, next) => {
      if (PortalSettingsProvider.getAccountType() !== AccountType.Business) {
        next('/');
        return;
      }

      next();
    }
  },
  {
    path: '/cameras/:id',
    name: 'EditCamera',
    component: CameraDetailsPage,
    meta: { pageTitleKey: 'EditCameraTitle' },
    beforeEnter: async (to, from, next) => {
      if (PortalSettingsProvider.getAccountType() !== AccountType.Business) {
        next('/');
        return;
      }

      next();
    }
  },
  {
    path: '/users',
    name: 'Users',
    component: UsersPage,
    meta: { pageTitleKey: 'UsersTitle' },
    props: {
      pageMode: pageMode.edit
    },
    beforeEnter: async (to, from, next) => {
      const contextData = useContextDataStore();
      if (!contextData.userData?.isBusinessAdminUser) {
        next('/');
        return;
      }

      next();
    }
  },
  {
    path: '/users/new',
    name: 'AddUser',
    component: UserDetails,
    meta: { pageTitleKey: 'AddUserTitle' },
    props: {
      pageMode: pageMode.add
    },
    beforeEnter: async (to, from, next) => {
      const contextData = useContextDataStore();
      if (!contextData.userData?.isBusinessAdminUser) {
        next('/');
        return;
      }

      next();
    }
  },
  {
    path: '/groups',
    name: 'Groups',
    component: GroupsPage,
    meta: { pageTitleKey: 'GroupsTitle' },
    beforeEnter: async (to, from, next) => {
      const contextData = useContextDataStore();
      if (!contextData.userData?.isBusinessAdminUser) {
        next('/');
        return;
      }

      next();
    }
  },
  {
    path: '/groups/new',
    name: 'AddGroup',
    component: GroupDetails,
    meta: { pageTitleKey: 'AddGroupTitle' },
    props: {
      pageMode: pageMode.add
    },
    beforeEnter: async (to, from, next) => {
      const contextData = useContextDataStore();
      if (!contextData.userData?.isBusinessAdminUser) {
        next('/');
        return;
      }

      next();
    }
  },
  {
    path: '/my-account',
    name: 'MyAccount',
    component: MyAccountPage,
    meta: { pageTitleKey: 'MyAccount' }
  },
  {
    path: '/error',
    name: 'PortalError',
    component: PortalError,
    meta: { pageTitleKey: 'PortalError' },
    beforeEnter: (to, from, next) => {
      const contextData = useContextDataStore();
      if (contextData.portalStartupError === PortalStartupErrorType.None) {
        next('/');
      } else {
        next();
      }
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotAvailable',
    component: PageNotAvailable
  }
];

export default routes;

